/**
 * "The user chose a company to act inside" — one behaviour, wherever the choice is offered.
 * Modelled on `useLanguageChoice.ts` for the same reason: two copies is how one of them quietly
 * stops doing the half nobody looks at.
 *
 * **`clear()`, not `invalidateQueries()`.** Query keys in `app/queries.ts` carry no organisation
 * — `['decas', query]`, `['users', query]`, `['audit-events', query]` — so after a switch the cache
 * holds the previous company's rows under keys the new company reuses. Invalidation marks them
 * stale and refetches while `useQuery` keeps returning the **previous data** until the refetch
 * lands, which on a normal screen is the feature and here means another client company's documents
 * on screen. Clearing removes them; the flicker is the correct trade.
 *
 * The alternative — putting the organisation into every key — is a larger change that leaves the
 * same data in memory under a different key, and buys nothing the portal needs today.
 *
 * **Choosing the company already chosen clears nothing** — the «Empresa» menu offers the ticked row
 * too. The rows in the cache are that company's, so clearing them would only read every delivery
 * photograph on screen again, and each read is an audit row (#160, `useDeliveryPhoto`). What it
 * does take is a name changed since the company was chosen: the bar and the admin screens name the
 * company from the stored choice, not from a read. Chosen again under the same name, nothing at all
 * changes, and the store's listeners are not called.
 */

import { useCallback, useSyncExternalStore } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import {
  getOrganisationContext,
  setOrganisationContext,
  subscribeOrganisation,
  type OrganisationContext,
} from './organisation.ts'

export function useOrganisationChoice(): {
  organisation: OrganisationContext | null
  choose: (next: OrganisationContext | null) => void
} {
  const client = useQueryClient()
  // The store lives outside React (`app/api.ts` reads it at module scope), so React subscribes to
  // it rather than owning it. The snapshot is referentially stable between changes, which is what
  // `useSyncExternalStore` requires.
  const organisation = useSyncExternalStore(subscribeOrganisation, getOrganisationContext)

  const choose = useCallback(
    (next: OrganisationContext | null) => {
      const current = getOrganisationContext()
      if ((next?.id ?? null) === (current?.id ?? null)) {
        if (next && current && next.name !== current.name) setOrganisationContext(next)
        return
      }
      setOrganisationContext(next)
      client.clear()
    },
    [client],
  )

  return { organisation, choose }
}
